import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Button, Divider, Empty, List, Popover, Spin, Tag, Typography,
} from 'antd';
import {
  BellOutlined, CheckOutlined, CloseOutlined,
  InfoCircleOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined,
} from '@ant-design/icons';
import api from '../../config/api';

const { Text } = Typography;

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: number; // 0=Info 1=Success 2=Warning 3=Error
  link?: string;
  isRead: boolean;
  createdAt: string;
}

const TYPE_CONFIG = {
  0: { icon: <InfoCircleOutlined style={{ color: '#56B0FF' }} />,  tag: 'blue',   label: 'Info'    },
  1: { icon: <CheckCircleOutlined style={{ color: '#16a34a' }} />, tag: 'green',  label: 'Success' },
  2: { icon: <WarningOutlined style={{ color: '#d97706' }} />,     tag: 'orange', label: 'Warning' },
  3: { icon: <CloseCircleOutlined style={{ color: '#dc2626' }} />, tag: 'red',    label: 'Error'   },
};

const timeAgo = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationsDropdown: React.FC = () => {
  const navigate = useNavigate();

  const [open,          setOpen]         = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount,   setUnreadCount]  = useState(0);
  const [loading,       setLoading]      = useState(false);
  const [markingAll,    setMarkingAll]   = useState(false);

  // Track previous unread count to detect new arrivals
  const prevCountRef = useRef(0);

  /**
   * Single refresh function that:
   *  1. Fetches the unread count
   *  2. If the count has increased (new notification arrived), also fetches
   *     the full list so the popover shows fresh items immediately — no manual
   *     page refresh needed.
   *  3. force=true always refreshes the list (used when opening the popover).
   */
  const refresh = useCallback(async (force = false) => {
    try {
      const countRes = await api.get('/notifications/unread-count');
      const newCount: number = countRes.data?.data ?? 0;
      const hasNew = newCount > prevCountRef.current;
      prevCountRef.current = newCount;
      setUnreadCount(newCount);

      if (hasNew || force) {
        setLoading(true);
        try {
          const listRes = await api.get('/notifications?page=1&pageSize=10');
          setNotifications(listRes.data?.data?.items ?? []);
        } finally {
          setLoading(false);
        }
      }
    } catch { /* non-critical */ }
  }, []);

  // Poll every 15 s; pause when the tab is hidden to save bandwidth
  useEffect(() => {
    refresh(true); // initial load

    const tick = () => { if (document.visibilityState === 'visible') refresh(); };
    const interval = setInterval(tick, 60_000);

    // Also re-check immediately when the user switches back to this tab
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [refresh]);

  const handleOpenChange = (visible: boolean) => {
    setOpen(visible);
    if (visible) refresh(true); // force-refresh list when popover opens
  };

  const markRead = async (id: string) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { /* ignore */ } finally {
      setMarkingAll(false);
    }
  };

  const dismiss = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation(); // prevent Popover from closing on X click

    // Optimistic update — remove immediately so the UI responds instantly
    const removed = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (removed && !removed.isRead) setUnreadCount(c => Math.max(0, c - 1));

    try {
      await api.delete(`/notifications/${id}`);
    } catch {
      // Restore if the server call fails
      if (removed) {
        setNotifications(prev =>
          [...prev, removed].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        );
        if (!removed.isRead) setUnreadCount(c => c + 1);
      }
    }
  };

  const handleClick = (n: NotificationItem) => {
    if (!n.isRead) markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const content = (
    <div style={{ width: 360 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px 8px' }}>
        <Text strong style={{ fontSize: 15 }}>
          Notifications {unreadCount > 0 && <Badge count={unreadCount} style={{ marginLeft: 6 }} />}
        </Text>
        {unreadCount > 0 && (
          <Button
            type="link" size="small"
            icon={<CheckOutlined />}
            loading={markingAll}
            onClick={markAllRead}
            style={{ padding: 0, fontSize: 12 }}
          >
            Mark all read
          </Button>
        )}
      </div>
      <Divider style={{ margin: 0 }} />

      {/* Body */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0' }}><Spin /></div>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No notifications"
          style={{ padding: '32px 0' }}
        />
      ) : (
        <List
          style={{ maxHeight: 380, overflowY: 'auto' }}
          dataSource={notifications}
          renderItem={n => {
            const cfg = TYPE_CONFIG[n.type as 0 | 1 | 2 | 3] ?? TYPE_CONFIG[0];
            return (
              <List.Item
                style={{
                  padding: '10px 16px',
                  cursor: n.link ? 'pointer' : 'default',
                  background: n.isRead ? 'transparent' : '#f0f7ff',
                  transition: 'background 0.15s',
                }}
                onClick={() => handleClick(n)}
                onMouseEnter={e => { if (n.link) (e.currentTarget as HTMLElement).style.background = n.isRead ? '#f8fafc' : '#e0f0ff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = n.isRead ? 'transparent' : '#f0f7ff'; }}
              >
                <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                  <div style={{ flexShrink: 0, paddingTop: 2, fontSize: 18 }}>{cfg.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Text strong style={{ fontSize: 13, color: '#0f172a' }}>{n.title}</Text>
                      <Button
                        type="text" size="small" shape="circle"
                        icon={<CloseOutlined style={{ fontSize: 10 }} />}
                        onClick={e => dismiss(n.id, e)}
                        style={{ flexShrink: 0, width: 20, height: 20, minWidth: 20, color: '#94a3b8' }}
                      />
                    </div>
                    <Text style={{ fontSize: 12, color: '#64748b', display: 'block', lineHeight: 1.5, marginTop: 2 }}>
                      {n.message}
                    </Text>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                      <Tag color={cfg.tag} style={{ borderRadius: 20, fontSize: 10, padding: '0 6px', margin: 0 }}>
                        {cfg.label}
                      </Tag>
                      <Text style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(n.createdAt)}</Text>
                      {!n.isRead && (
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#56B0FF', display: 'inline-block' }} />
                      )}
                    </div>
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
      arrow={false}
      overlayStyle={{ padding: 0 }}
      overlayInnerStyle={{ padding: 0, borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}
    >
      <Badge count={unreadCount} size="small" offset={[-2, 2]} style={{ cursor: 'pointer' }}>
        <Button
          type="text"
          shape="circle"
          icon={
            <BellOutlined style={{ fontSize: 18, color: unreadCount > 0 ? '#56B0FF' : undefined }} />
          }
        />
      </Badge>
    </Popover>
  );
};

export default NotificationsDropdown;
